import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
// import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../utils/api';
import { Draft, Team } from '../types';
import { 
  Container, 
  Card, 
  Button, 
  Input,
  Label,
  LoadingSpinner,
  colors,
  spacing,
  typography,
  media
} from '../styles/GlobalStyles';
import { 
  ArrowLeft,
  Edit,
  Save,
  X,
  Filter,
  DollarSign,
  Trophy
} from 'lucide-react';
import toast from 'react-hot-toast';

const AdminPicksContainer = styled.div`
  min-height: calc(100vh - 80px);
  background: #000000;
`;

const PageHeader = styled.div`
  margin-bottom: ${spacing[8]};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PageTitle = styled.h1`
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  margin: 0;
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

const ControlsSection = styled(Card)`
  padding: ${spacing[6]};
  margin-bottom: ${spacing[6]};
`;

const ControlsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr auto auto;
  gap: ${spacing[4]};
  align-items: end;

  ${media.tablet} {
    grid-template-columns: 1fr;
    gap: ${spacing[3]};
  }
`;

const SearchInput = styled(Input)`
  flex: 1;
`;

const FilterSelect = styled.select`
  padding: ${spacing[3]};
  background: ${colors.grey[800]};
  border: 1px solid ${colors.grey[600]};
  border-radius: ${spacing[2]};
  color: ${colors.white};
  font-size: ${typography.fontSize.base};
  min-width: 150px;
`;

const PicksTable = styled.div`
  background: ${colors.grey[900]};
  border-radius: ${spacing[3]};
  overflow: hidden;
  border: 1px solid ${colors.grey[700]};
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 120px 100px 120px 100px 120px 80px;
  gap: ${spacing[4]};
  padding: ${spacing[4]};
  background: ${colors.grey[800]};
  border-bottom: 1px solid ${colors.grey[700]};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  font-size: ${typography.fontSize.sm};

  ${media.tablet} {
    grid-template-columns: 60px 1fr 80px 60px 80px 60px 80px 60px;
    gap: ${spacing[2]};
    padding: ${spacing[3]};
    font-size: ${typography.fontSize.xs};
  }
`;

const TableRow = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isEditing'
})<{ isEditing?: boolean }>`
  display: grid;
  grid-template-columns: 80px 1fr 120px 100px 120px 100px 120px 80px;
  gap: ${spacing[4]};
  padding: ${spacing[4]};
  border-bottom: 1px solid ${colors.grey[700]};
  align-items: center;
  background: ${props => props.isEditing ? colors.green + '10' : 'transparent'};
  transition: all 0.2s ease;

  &:hover {
    background: ${colors.grey[800]};
  }

  ${media.tablet} {
    grid-template-columns: 60px 1fr 80px 60px 80px 60px 80px 60px;
    gap: ${spacing[2]};
    padding: ${spacing[3]};
  }
`;

const TableCell = styled.div`
  color: ${colors.white};
  font-size: ${typography.fontSize.sm};
  display: flex;
  align-items: center;
  gap: ${spacing[1]};

  ${media.tablet} {
    font-size: ${typography.fontSize.xs};
  }
`;

const TableCellInput = styled(Input)`
  font-size: ${typography.fontSize.sm};
  padding: ${spacing[2]};

  ${media.tablet} {
    font-size: ${typography.fontSize.xs};
    padding: ${spacing[1]};
  }
`;

const TableCellSelect = styled.select`
  font-size: ${typography.fontSize.sm};
  padding: ${spacing[2]};
  background: ${colors.grey[800]};
  border: 1px solid ${colors.grey[600]};
  border-radius: ${spacing[1]};
  color: ${colors.white};

  ${media.tablet} {
    font-size: ${typography.fontSize.xs};
    padding: ${spacing[1]};
  }
`;

const ActionButton = styled(Button)`
  padding: ${spacing[1]} ${spacing[2]};
  font-size: ${typography.fontSize.xs};
  min-width: auto;

  ${media.tablet} {
    padding: ${spacing[1]};
  }
`;

const StatusBadge = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== 'status'
})<{ status: string }>`
  padding: ${spacing[1]} ${spacing[2]};
  border-radius: ${spacing[1]};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.medium};
  text-transform: uppercase;
  
  ${props => {
    switch (props.status) {
      case 'active':
        return `
          background: ${colors.success}20;
          color: ${colors.success};
        `;
      case 'completed':
        return `
          background: ${colors.info}20;
          color: ${colors.info};
        `;
      default:
        return `
          background: ${colors.grey[700]};
          color: ${colors.grey[300]};
        `;
    }
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${spacing[12]};
  color: ${colors.grey[400]};
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${spacing[4]};
  margin-bottom: ${spacing[6]};
`;

const StatCard = styled(Card)`
  padding: ${spacing[4]};
  text-align: center;
`;

const StatValue = styled.div`
  font-size: ${typography.fontSize['2xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.white};
  margin-bottom: ${spacing[1]};
`;

const StatLabel = styled.div`
  font-size: ${typography.fontSize.sm};
  color: ${colors.grey[400]};
`;

const Modal = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen'
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${spacing[4]};
`;

const ModalContent = styled(Card)`
  width: 100%;
  max-width: 500px;
  padding: ${spacing[6]};
`;

const ModalTitle = styled.h3`
  font-size: ${typography.fontSize.xl};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.white};
  margin-bottom: ${spacing[4]};
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${spacing[3]};
  justify-content: flex-end;
  margin-top: ${spacing[6]};
`;

interface Pick {
  _id: string;
  pickNumber: number;
  round: number;
  teamId: {
    _id: string;
    name: string;
    teamNumber: number;
  };
  playerId: string;
  playerName: string;
  position: string;
  nflTeam: string;
  cost: number;
  timestamp: string;
  draftName: string;
  draftId: string;
  draftStatus: string;
}

interface EditingPick {
  pickNumber: number;
  playerId: string;
  playerName: string;
  position: string;
  nflTeam: string;
  cost: number;
  teamId: string;
}

const AdminPicksPage: React.FC = () => {
  // const { user } = useAuth();
  const [picks, setPicks] = useState<Pick[]>([]);
  const [drafts, setDrafts] = useState<Draft[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDraft, setSelectedDraft] = useState<string>('');
  const [editingPick, setEditingPick] = useState<EditingPick | null>(null);
  const [saving, setSaving] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [picksResponse, draftsResponse, teamsResponse] = await Promise.all([
        apiClient.drafts.getRecentPicks({ limit: 100 }),
        apiClient.drafts.getAll(),
        apiClient.teams.getAll()
      ]);

      if (picksResponse.success) {
        setPicks(picksResponse.data.picks);
      }
      if (draftsResponse.success) {
        setDrafts(draftsResponse.data.drafts);
      }
      if (teamsResponse.success) {
        setTeams(teamsResponse.data.teams);
      }
    } catch (error) {
      console.error('Failed to load picks data:', error);
      toast.error('Failed to load picks data');
    } finally {
      setLoading(false);
    }
  };



  const handleEditPick = (pick: Pick) => {
    setEditingPick({
      pickNumber: pick.pickNumber,
      playerId: pick.playerId,
      playerName: pick.playerName,
      position: pick.position,
      nflTeam: pick.nflTeam,
      cost: pick.cost,
      teamId: pick.teamId._id
    });
  };

  const handleCancelEdit = () => {
    setEditingPick(null);
  };

  const handleSavePick = async () => {
    if (!editingPick) return;

    try {
      setSaving(true);
      // Find the draft ID for this pick
      const pick = picks.find(p => p.pickNumber === editingPick.pickNumber);
      if (!pick) {
        toast.error('Pick not found');
        return;
      }

      const response = await apiClient.drafts.updatePick(
        pick.draftId,
        editingPick.pickNumber,
        editingPick
      );

      if (response.success) {
        toast.success('Pick updated successfully');
        setEditingPick(null);
        setShowConfirmModal(false);
        await loadData();
      }
    } catch (error) {
      console.error('Failed to update pick:', error);
      toast.error('Failed to update pick');
    } finally {
      setSaving(false);
    }
  };

  const confirmSavePick = () => {
    setShowConfirmModal(true);
  };

  const filteredPicks = picks.filter(pick => {
    const matchesSearch = searchTerm === '' || 
      pick.playerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pick.teamId.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      pick.nflTeam.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesDraft = selectedDraft === '' || pick.draftId === selectedDraft;
    
    return matchesSearch && matchesDraft;
  });

  const stats = {
    totalPicks: picks.length,
    totalDrafts: drafts.length,
    activeDrafts: drafts.filter(d => d.status === 'active').length,
    completedDrafts: drafts.filter(d => d.status === 'completed').length,
    averageCost: picks.length > 0 ? Math.round(picks.reduce((sum, p) => sum + p.cost, 0) / picks.length) : 0
  };

  if (loading) {
    return (
      <AdminPicksContainer>
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <LoadingSpinner size="lg" />
          </div>
        </Container>
      </AdminPicksContainer>
    );
  }

  return (
    <AdminPicksContainer>
      <Container>
        <PageHeader>
          <PageTitle>Draft Picks Management</PageTitle>
          <BackButton variant="secondary" onClick={() => window.history.back()}>
            <ArrowLeft size={16} />
            Back
          </BackButton>
        </PageHeader>

        <StatsGrid>
          <StatCard>
            <StatValue>{stats.totalPicks}</StatValue>
            <StatLabel>Total Picks</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.totalDrafts}</StatValue>
            <StatLabel>Total Drafts</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.activeDrafts}</StatValue>
            <StatLabel>Active Drafts</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>{stats.completedDrafts}</StatValue>
            <StatLabel>Completed Drafts</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue>${stats.averageCost}</StatValue>
            <StatLabel>Average Cost</StatLabel>
          </StatCard>
        </StatsGrid>

        <ControlsSection>
          <ControlsGrid>
            <div>
              <Label htmlFor="search">Search Picks</Label>
              <SearchInput
                id="search"
                type="text"
                placeholder="Search by player name, team, or NFL team..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div>
              <Label htmlFor="draftFilter">Filter by Draft</Label>
              <FilterSelect
                id="draftFilter"
                value={selectedDraft}
                onChange={(e) => setSelectedDraft(e.target.value)}
              >
                <option value="">All Drafts</option>
                {drafts.map(draft => (
                  <option key={draft._id} value={draft._id}>
                    {draft.name} ({draft.status})
                  </option>
                ))}
              </FilterSelect>
            </div>

            <Button onClick={loadData}>
              <Filter size={16} />
              Refresh
            </Button>
          </ControlsGrid>
        </ControlsSection>

        <PicksTable>
          <TableHeader>
            <div>Pick #</div>
            <div>Player</div>
            <div>Position</div>
            <div>NFL Team</div>
            <div>Fantasy Team</div>
            <div>Cost</div>
            <div>Draft</div>
            <div>Actions</div>
          </TableHeader>

          {filteredPicks.length > 0 ? (
            filteredPicks.map(pick => (
              <TableRow key={`${pick.draftId}-${pick.pickNumber}`} isEditing={editingPick?.pickNumber === pick.pickNumber}>
                <TableCell>
                  #{pick.pickNumber}
                </TableCell>
                
                <TableCell>
                  {editingPick?.pickNumber === pick.pickNumber ? (
                    <TableCellInput
                      value={editingPick.playerName}
                      onChange={(e) => setEditingPick(prev => prev ? { ...prev, playerName: e.target.value } : null)}
                      placeholder="Player name"
                    />
                  ) : (
                    <div>
                      <div style={{ fontWeight: typography.fontWeight.medium }}>{pick.playerName}</div>
                      <div style={{ fontSize: typography.fontSize.xs, color: colors.grey[400] }}>ID: {pick.playerId}</div>
                    </div>
                  )}
                </TableCell>
                
                <TableCell>
                  {editingPick?.pickNumber === pick.pickNumber ? (
                    <TableCellSelect
                      value={editingPick.position}
                      onChange={(e) => setEditingPick(prev => prev ? { ...prev, position: e.target.value } : null)}
                    >
                      <option value="QB">QB</option>
                      <option value="RB">RB</option>
                      <option value="WR">WR</option>
                      <option value="TE">TE</option>
                      <option value="K">K</option>
                      <option value="DEF">DEF</option>
                    </TableCellSelect>
                  ) : (
                    pick.position
                  )}
                </TableCell>
                
                <TableCell>
                  {editingPick?.pickNumber === pick.pickNumber ? (
                    <TableCellInput
                      value={editingPick.nflTeam}
                      onChange={(e) => setEditingPick(prev => prev ? { ...prev, nflTeam: e.target.value } : null)}
                      placeholder="NFL Team"
                    />
                  ) : (
                    pick.nflTeam
                  )}
                </TableCell>
                
                <TableCell>
                  {editingPick?.pickNumber === pick.pickNumber ? (
                    <TableCellSelect
                      value={editingPick.teamId}
                      onChange={(e) => setEditingPick(prev => prev ? { ...prev, teamId: e.target.value } : null)}
                    >
                      {teams.map(team => (
                        <option key={team._id} value={team._id}>
                          {team.name} (#{team.teamNumber})
                        </option>
                      ))}
                    </TableCellSelect>
                  ) : (
                    <div>
                      <div>{pick.teamId.name}</div>
                      <div style={{ fontSize: typography.fontSize.xs, color: colors.grey[400] }}>#{pick.teamId.teamNumber}</div>
                    </div>
                  )}
                </TableCell>
                
                <TableCell>
                  {editingPick?.pickNumber === pick.pickNumber ? (
                    <TableCellInput
                      type="number"
                      value={editingPick.cost}
                      onChange={(e) => setEditingPick(prev => prev ? { ...prev, cost: parseInt(e.target.value) || 0 } : null)}
                      placeholder="Cost"
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: spacing[1] }}>
                      <DollarSign size={12} />
                      {pick.cost}
                    </div>
                  )}
                </TableCell>
                
                <TableCell>
                  <div>
                    <div style={{ fontSize: typography.fontSize.xs }}>{pick.draftName}</div>
                    <StatusBadge status={pick.draftStatus}>
                      {pick.draftStatus}
                    </StatusBadge>
                  </div>
                </TableCell>
                
                <TableCell>
                  {editingPick?.pickNumber === pick.pickNumber ? (
                                         <div style={{ display: 'flex', gap: spacing[1] }}>
                                               <ActionButton
                          size="sm"
                          onClick={confirmSavePick}
                          disabled={saving}
                        >
                          {saving ? <LoadingSpinner size="sm" /> : <Save size={12} />}
                        </ActionButton>
                       <ActionButton
                         size="sm"
                         variant="secondary"
                         onClick={handleCancelEdit}
                       >
                         <X size={12} />
                       </ActionButton>
                     </div>
                  ) : (
                    <ActionButton
                      size="sm"
                      variant="secondary"
                      onClick={() => handleEditPick(pick)}
                    >
                      <Edit size={12} />
                    </ActionButton>
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <EmptyState>
              <Trophy size={48} style={{ marginBottom: spacing[4], opacity: 0.5 }} />
              <p>No picks found matching your criteria</p>
            </EmptyState>
                     )}
         </PicksTable>

         {/* Confirmation Modal */}
         <Modal isOpen={showConfirmModal}>
           <ModalContent>
             <ModalTitle>Confirm Pick Update</ModalTitle>
             
             <div style={{ marginBottom: spacing[6] }}>
               <p style={{ color: colors.grey[300], marginBottom: spacing[4] }}>
                 Are you sure you want to update this pick? This action will:
               </p>
               <ul style={{ 
                 color: colors.grey[300], 
                 marginLeft: spacing[4],
                 marginBottom: spacing[4]
               }}>
                 <li>Update the player information</li>
                 <li>Modify team rosters accordingly</li>
                 <li>Update player draft status</li>
                 <li>Emit real-time updates to all connected users</li>
               </ul>
               <p style={{ color: colors.warning, fontSize: typography.fontSize.sm }}>
                 This action cannot be easily undone.
               </p>
             </div>
             
             <ModalActions>
               <Button 
                 type="button" 
                 variant="secondary"
                 onClick={() => setShowConfirmModal(false)}
                 disabled={saving}
               >
                 Cancel
               </Button>
               <Button 
                 onClick={handleSavePick}
                 disabled={saving}
               >
                 {saving ? 'Updating...' : 'Update Pick'}
               </Button>
             </ModalActions>
           </ModalContent>
         </Modal>
       </Container>
     </AdminPicksContainer>
   );
 };

export default AdminPicksPage;
